import { useEffect, useState } from 'react';
import { NavLink, useNavigate, useParams } from 'react-router-dom';
import {
  clients, studies as studiesApi, offers as offersApi, contracts as contractsApi,
  fieldWorks as fieldWorksApi, invoices as invoicesApi,
  type Contract, type FieldWork,
} from '@/api/endpoints';
import type { Client, StudySummary, Offer, Invoice } from '@/types';
import { Icon } from '@/components/Icon';
import { ClientFormModal } from '@/components/ClientFormModal';
import { ArchiveFilesCard } from '@/components/ArchiveFilesCard';
import { ClientRelationsCards } from '@/components/ClientRelationsCards';
import { useAuth } from '@/auth/AuthContext';
import { formatCurrency } from '@/lib/money';

export function ClientDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const clientId = id ? Number(id) : null;
  const { user } = useAuth();
  // Geolog nie widzi ofert i umów; OWNER+ADMIN+OFFICE/ASSISTANT widzą zgodnie z macierzą uprawnień.
  const canSeeOffers = user?.permissions?.includes('OFFER_VIEW') ?? false;
  const canCreateOffers = user?.permissions?.includes('OFFER_CREATE') ?? false;
  const canSeeContracts = user?.permissions?.includes('CONTRACT_VIEW') ?? false;
  // Ceny — tylko OWNER+ADMIN. OFFICE widzi listy ofert/umów ale bez kwot.
  const canSeePricing = user?.permissions?.includes('PRICING_VIEW') ?? false;
  const canSeeWorks = user?.permissions?.includes('FIELDWORK_VIEW') ?? false;
  // Mapa przeglądu pokazuje i ujęcia, i prace — wystarczy jedno z uprawnień.
  const canSeeMap = canSeeWorks || (user?.permissions?.includes('INTAKE_VIEW') ?? false);
  const canSeeFinance = user?.permissions?.includes('FINANCE_VIEW') ?? false;
  const canEditClient = user?.permissions?.includes('CLIENT_EDIT') ?? false;

  const [client, setClient] = useState<Client | null>(null);
  const [studies, setStudies] = useState<StudySummary[] | null>(null);
  const [offers, setOffers] = useState<Offer[] | null>(null);
  const [contracts, setContracts] = useState<Contract[] | null>(null);
  const [works, setWorks] = useState<FieldWork[] | null>(null);
  const [clientInvoices, setClientInvoices] = useState<Invoice[] | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [editOpen, setEditOpen] = useState(false);

  const reload = () => {
    if (!clientId) return;
    setErr(null);
    const tasks: Promise<unknown>[] = [
      clients.get(clientId).then(setClient),
      studiesApi.list(undefined, undefined, clientId).then(setStudies),
    ];
    // Nie wywołujemy /api/offers ani /api/contracts dla użytkowników bez uprawnień —
    // backend zwróci 403, ale po co generować błąd w konsoli.
    if (canSeeOffers) tasks.push(offersApi.list(undefined, clientId).then(setOffers));
    if (canSeeContracts) tasks.push(contractsApi.list({ clientId }).then(setContracts));
    if (canSeeWorks) tasks.push(fieldWorksApi.list({ clientId }).then(setWorks));
    // Faktury sprzedażowe: „ile ten klient jest nam winien" to pytanie zadawane na karcie
    // klienta, nie na liście 2000 faktur. Bramka FINANCE_VIEW — geolog tu nic nie zobaczy.
    if (canSeeFinance) tasks.push(invoicesApi.list('SALES', undefined, clientId).then(setClientInvoices));
    Promise.all(tasks).catch(e => setErr(e?.message ?? String(e)));
  };

  useEffect(() => {
    setClient(null); setStudies(null); setOffers(null); setContracts(null);
    setWorks(null); setClientInvoices(null);
    reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientId]);

  if (err) return <div className="text-red-500">Błąd: {err}</div>;
  if (!client) return <div className="text-ink-400">Ładowanie…</div>;

  return (
    <div className="max-w-[1400px]">
      <header className="flex items-start justify-between mb-5 gap-4">
        <div className="min-w-0">
          <div className="text-[12px] text-ink-500 mb-1">
            <NavLink to="/klienci" className="hover:text-ink-800">Klienci</NavLink>
            <span className="mx-1.5">/</span>
            <span className="text-ink-700">{client.name}</span>
          </div>
          <h1 className="m-0 truncate">
            {client.name}
            {client.clientStatusName && (
              <span className={'pill ml-3 align-middle text-[11px] ' + (client.clientStatus === 'AKTYWNY' ? 'pill-green'
                : client.clientStatus === 'POTENCJALNY' ? 'pill-amber' : '')}>{client.clientStatusName}</span>
            )}
          </h1>
          <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-[12px] text-ink-500 mt-2">
            {client.nip && <span><span className="text-ink-400">NIP:</span> <span className="font-mono text-ink-700">{client.nip}</span></span>}
            {client.email && <span><Icon name="bell" className="w-3 h-3 inline mr-1"/>{client.email}</span>}
            {client.phone && <span className="font-mono">{client.phone}</span>}
            {(client.addressLine1 || client.city) && (
              <span>
                {client.addressLine1}
                {client.addressLine1 && (client.city || client.postalCode) ? ', ' : ''}
                {[client.postalCode, client.city].filter(Boolean).join(' ')}
              </span>
            )}
            {(client.gmina || client.county || client.voivodeship) && (
              <span className="text-ink-500">
                {[
                  client.gmina && `gm. ${client.gmina}`,
                  client.county && `pow. ${client.county}`,
                  client.voivodeship && `woj. ${client.voivodeship}`,
                ].filter(Boolean).join(' · ')}
              </span>
            )}
          </div>
          {client.remarks && <div className="text-[12px] text-ink-600 mt-2 italic">{client.remarks}</div>}
        </div>
        <div className="flex gap-2 shrink-0">
          {canCreateOffers && (
            <button onClick={() => navigate(`/oferta-wizard?clientId=${client.id}`)} className="btn btn-primary btn-sm">
              <Icon name="plus" className="w-3.5 h-3.5"/> Nowa oferta
            </button>
          )}
          <button onClick={() => setEditOpen(true)} className="btn btn-secondary btn-sm">
            <Icon name="edit" className="w-3.5 h-3.5"/> Edytuj
          </button>
          <NavLink to="/klienci" className="btn btn-secondary btn-sm">← Wszyscy</NavLink>
        </div>
      </header>

      <div className="grid gap-4 mb-4"
           style={{ gridTemplateColumns: `repeat(${1 + (canSeeWorks ? 1 : 0) + (canSeeOffers ? 1 : 0)
                                                     + (canSeeContracts ? 1 : 0) + (canSeeFinance ? 1 : 0)}, minmax(0,1fr))` }}>
        <KpiCard label="Opracowania" count={studies?.length ?? 0}/>
        {canSeeWorks && <KpiCard label="Prace terenowe" count={works?.length ?? 0}/>}
        {canSeeOffers && <KpiCard label="Oferty" count={offers?.length ?? 0}/>}
        {canSeeContracts && <KpiCard label="Umowy" count={contracts?.length ?? 0}/>}
        {canSeeFinance && <KpiCard label="Faktury" count={clientInvoices?.length ?? 0}/>}
      </div>

      <ClientRelationsCards clientId={client.id} canEdit={canEditClient} onProfileSaved={reload}/>

      {/* Lokalizacja: pinezka wpisana ręcznie, niezależna od adresu. Pokazujemy ją zawsze —
          także gdy jej nie ma — bo „klient nie widać na mapie prac" zaczyna się właśnie tutaj. */}
      <div className="card mb-4">
        <div className="card-head">
          <div className="card-title">Lokalizacja na mapie</div>
          {canSeeMap && (
            <NavLink to={`/mapa-przeglad?clientId=${client.id}`}
                     className="ml-auto text-[12px] text-water-600 hover:underline">
              Pokaż na mapie →
            </NavLink>
          )}
        </div>
        <div className="card-body text-[13px]">
          {client.latitude != null && client.longitude != null ? (
            <div className="flex items-center gap-4 flex-wrap">
              <span className="font-mono text-ink-800">
                {Number(client.latitude).toFixed(6)}, {Number(client.longitude).toFixed(6)}
              </span>
              {client.locationNote && <span className="text-ink-500">{client.locationNote}</span>}
              <a href={`https://www.google.com/maps?q=${client.latitude},${client.longitude}`}
                 target="_blank" rel="noreferrer"
                 className="text-water-600 hover:underline text-[12px]">Otwórz w Google Maps ↗</a>
            </div>
          ) : (
            <div className="text-ink-500">
              Brak współrzędnych — klient nie pojawi się na mapie prac.{' '}
              <button onClick={() => setEditOpen(true)} className="text-water-600 hover:underline">
                Uzupełnij w edycji
              </button>. Punkt nie musi pokrywać się z adresem siedziby.
            </div>
          )}
        </div>
      </div>

      {canSeeWorks && (
      <div className="card mb-4">
        <div className="card-head">
          <div className="card-title">Prace terenowe</div>
          <NavLink to={`/prace?clientId=${client.id}`} className="ml-auto text-[12px] text-water-600 hover:underline">
            Otwórz w rejestrze →
          </NavLink>
        </div>
        <div className="card-body !p-0">
          {works == null && <div className="p-4 text-ink-400">Ładowanie…</div>}
          {works && works.length === 0 && <div className="p-4 text-ink-400">Brak prac dla tego klienta.</div>}
          {works && works.length > 0 && (
            <table className="table-hw">
              <thead>
                <tr>
                  <th>Lp.</th><th>Opis prac</th><th>Rodzaj</th><th>Status</th>
                  <th>Wykonano</th>{canSeePricing && <th>Netto</th>}<th></th>
                </tr>
              </thead>
              <tbody>
                {works.map(w => (
                  <tr key={w.id} className="hover:bg-ink-50 cursor-pointer" onClick={() => navigate(`/prace/${w.id}`)}>
                    <td className="font-mono text-[12px] text-ink-500">{w.seqNo}</td>
                    <td className="text-[13px] text-ink-800 truncate max-w-[340px]">
                      {w.title}
                      {w.wellLabel && <span className="text-ink-400"> · studnia {w.wellLabel}</span>}
                    </td>
                    <td><span className="pill pill-earth">{w.kindName}</span></td>
                    <td><span className={'pill pill-' + w.statusColor}>{w.statusName}</span></td>
                    <td className="font-mono text-[12px] text-ink-600">{w.firstDate ?? '—'}</td>
                    {canSeePricing && (
                      <td className="font-mono text-[12px] text-ink-700">{formatCurrency(w.netAmount)}</td>
                    )}
                    <td><Icon name="chevron" className="w-3 h-3 text-ink-400"/></td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
      )}

      {canSeeFinance && (
      <div className="card mb-4">
        <div className="card-head">
          <div className="card-title">Faktury sprzedażowe</div>
          <span className="ml-auto text-[12px] text-ink-400">{clientInvoices?.length ?? 0}</span>
        </div>
        <div className="card-body !p-0">
          {clientInvoices == null && <div className="p-4 text-ink-400">Ładowanie…</div>}
          {clientInvoices && clientInvoices.length === 0 && (
            <div className="p-4 text-ink-400 text-[13px]">
              Brak faktur przypisanych do tego kontrahenta. Faktury z KSeF dopasowują się po NIP-ie —
              jeśli klient nie ma wpisanego NIP-u, dopasowanie nie zadziała.
            </div>
          )}
          {clientInvoices && clientInvoices.length > 0 && (
            <table className="table-hw">
              <thead>
                <tr><th>Numer</th><th>Wystawiona</th><th>Termin</th><th>Brutto</th><th>Płatność</th><th></th></tr>
              </thead>
              <tbody>
                {clientInvoices.map(i => (
                  <tr key={i.id} className="hover:bg-ink-50 cursor-pointer" onClick={() => navigate(`/faktury/${i.id}`)}>
                    <td className="font-mono text-[12px] text-water-600">{i.invoiceNumber ?? i.ksefNumber ?? `#${i.id}`}</td>
                    <td className="font-mono text-[12px] text-ink-600">{i.issueDate ?? '—'}</td>
                    <td className="font-mono text-[12px] text-ink-600">{i.dueDate ?? '—'}</td>
                    <td className="font-mono text-[12px] text-ink-800">{formatCurrency(i.totalGross, i.currency)}</td>
                    <td><span className={'pill pill-' + i.paymentStatusColor}>{i.paymentStatusName}</span></td>
                    <td><Icon name="chevron" className="w-3 h-3 text-ink-400"/></td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
      )}

      <div className="card mb-4">
        <div className="card-head">
          <div className="card-title">Opracowania</div>
          <span className="ml-auto text-[12px] text-ink-400">{studies?.length ?? 0}</span>
        </div>
        <div className="card-body !p-0">
          {studies == null && <div className="p-4 text-ink-400">Ładowanie…</div>}
          {studies && studies.length === 0 && <div className="p-4 text-ink-400">Brak opracowań dla tego klienta.</div>}
          {studies && studies.length > 0 && (
            <table className="table-hw">
              <thead>
                <tr>
                  <th>Numer</th>
                  <th>Tytuł</th>
                  <th>Typ</th>
                  <th>Ujęcie</th>
                  <th>Status</th>
                  <th>Deadline</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {studies.map(o => (
                  <tr key={o.id} className="hover:bg-ink-50">
                    <td><NavLink to={`/studies/${o.id}`} className="font-mono text-[12px] text-water-600 no-underline hover:underline">{o.number}</NavLink></td>
                    <td className="text-[13px] text-ink-800 truncate max-w-[320px]">{o.title}</td>
                    <td><span className="pill pill-earth">{o.typeAbbreviation}-{o.typeCode}</span></td>
                    <td className="text-[12px] text-ink-700">
                      {o.intakeId
                        ? <NavLink to={`/intakes/${o.intakeId}`} className="text-water-600 no-underline hover:underline">{o.intakeName}</NavLink>
                        : '—'}
                    </td>
                    <td><span className={'pill pill-' + (o.statusColor || 'water')}>{o.statusName}</span></td>
                    <td className="font-mono text-[12px] text-ink-600">{o.deadline ?? '—'}</td>
                    <td><NavLink to={`/studies/${o.id}`}><Icon name="chevron" className="w-3 h-3 text-ink-400"/></NavLink></td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {canSeeOffers && (
      <div className="card mb-4">
        <div className="card-head">
          <div className="card-title">Oferty</div>
          <span className="ml-auto text-[12px] text-ink-400">{offers?.length ?? 0}</span>
        </div>
        <div className="card-body !p-0">
          {offers == null && <div className="p-4 text-ink-400">Ładowanie…</div>}
          {offers && offers.length === 0 && <div className="p-4 text-ink-400">Brak ofert dla tego klienta.</div>}
          {offers && offers.length > 0 && (
            <table className="table-hw">
              <thead>
                <tr>
                  <th>Numer</th>
                  <th>Tytuł</th>
                  <th>Typ</th>
                  <th>Status</th>
                  {canSeePricing && <th>Wartość brutto</th>}
                  <th>Wystawiona</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {offers.map(of => (
                  <tr key={of.id} className="hover:bg-ink-50">
                    <td><NavLink to={`/oferty/${of.id}`} className="font-mono text-[12px] text-water-600 no-underline hover:underline">{of.number}</NavLink></td>
                    <td className="text-[13px] text-ink-800 truncate max-w-[280px]">{of.title}</td>
                    <td><span className="pill pill-earth">{of.typeName}</span></td>
                    <td><span className="pill pill-water">{of.statusName}</span></td>
                    {canSeePricing && <td className="font-mono text-[12px] text-ink-700">{formatCurrency(of.totalGross)}</td>}
                    <td className="font-mono text-[12px] text-ink-500">{of.issueDate}</td>
                    <td><NavLink to={`/oferty/${of.id}`}><Icon name="chevron" className="w-3 h-3 text-ink-400"/></NavLink></td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
      )}

      {canSeeContracts && (
      <div className="card mb-4">
        <div className="card-head">
          <div className="card-title">Umowy</div>
          <span className="ml-auto text-[12px] text-ink-400">{contracts?.length ?? 0}</span>
        </div>
        <div className="card-body !p-0">
          {contracts == null && <div className="p-4 text-ink-400">Ładowanie…</div>}
          {contracts && contracts.length === 0 && <div className="p-4 text-ink-400">Brak umów dla tego klienta.</div>}
          {contracts && contracts.length > 0 && (
            <table className="table-hw">
              <thead>
                <tr>
                  <th>Numer</th>
                  <th>Wariant</th>
                  <th>Opracowanie</th>
                  {canSeePricing && <th>Kwota</th>}
                  <th>Podpisana</th>
                  <th>Status</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {contracts.map(u => (
                  <tr key={u.id}
                      className="hover:bg-ink-50 cursor-pointer"
                      onClick={() => navigate(`/umowa/${u.id}`)}>
                    <td className="font-mono text-[12px] text-water-600 hover:underline">{u.number}</td>
                    <td><span className="pill pill-earth">{u.wariantName}</span></td>
                    <td className="text-[12px] text-ink-700" onClick={e => e.stopPropagation()}>
                      {u.studies.length === 0 && '—'}
                      {u.studies.length === 1 && (
                        <NavLink to={`/studies/${u.studies[0].id}`} className="text-water-600 no-underline hover:underline">
                          {u.studies[0].number} — {u.studies[0].title}
                        </NavLink>
                      )}
                      {u.studies.length > 1 && (
                        <span className="text-ink-700">{u.studies.length} opracowań:{' '}
                          {u.studies.map((s, i) => (
                            <span key={s.id}>
                              {i > 0 && ', '}
                              <NavLink to={`/studies/${s.id}`} className="text-water-600 no-underline hover:underline">{s.number}</NavLink>
                            </span>
                          ))}
                        </span>
                      )}
                    </td>
                    {canSeePricing && <td className="font-mono text-[12px] text-ink-700">{formatCurrency(u.amount)}</td>}
                    <td className="font-mono text-[12px] text-ink-500">{u.signedDate ?? '—'}</td>
                    <td>{u.signed
                      ? <span className="pill pill-green">podpisana</span>
                      : (u.draft ? <span className="pill pill-amber">draft</span> : <span className="pill pill-water">zaakceptowana</span>)}</td>
                    <td><Icon name="chevron" className="w-3 h-3 text-ink-400"/></td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
      )}

      <div className="mb-4">
        <ArchiveFilesCard scope="client" scopeId={client.id} scopeName={client.name}/>
      </div>

      <ClientFormModal
        open={editOpen}
        initial={client}
        onClose={() => setEditOpen(false)}
        onSaved={() => { setEditOpen(false); reload(); }}
      />
    </div>
  );
}

function KpiCard({ label, count }: { label: string; count: number }) {
  return (
    <div className="card">
      <div className="card-body flex items-center gap-3">
        <div className="text-[28px] font-bold text-ink-800 leading-none">{count}</div>
        <div className="text-[12px] text-ink-500 uppercase tracking-wider">{label}</div>
      </div>
    </div>
  );
}
